import {
  MAX_PRODUCTION_ORDER_SHEET_NOTE_LENGTH,
  type ProductionOrderSheetFields,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { orderErrorMessage } from '@/shared/lib';
import { Button, formStyles, Input, Label } from '@/shared/ui';
import { sheetInputMessage } from '../model/sheet-input-message';
import { LineRowsEditor } from './line-rows-editor';
import { PrintProductionOrderButton } from './print-production-order-button';
import { SupplierFields } from './supplier-fields';

interface ProductionOrderSheetFormProps {
  readonly initial: ProductionOrderSheetFields;
  readonly onSave: (fields: ProductionOrderSheetFields) => Promise<unknown>;
  readonly printHref: string;
  readonly disabled?: boolean;
}

/**
 * 발주서 입력 폼 — 값은 폼이 들고, 마지막 저장(초기) 값과 다르면 출력 옆에 안내를 띄운다. `initial` 이 내용으로 바뀌면(저장 뒤
 * 다시 읽기) 그 값으로 처음부터 시작한다. 저장 실패는 폼 안에 알린다.
 */
export const ProductionOrderSheetForm = ({
  initial,
  onSave,
  printHref,
  disabled = false,
}: ProductionOrderSheetFormProps): ReactElement => {
  const [values, setValues] = useState(initial);
  const [syncedInitial, setSyncedInitial] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null);

  if (JSON.stringify(syncedInitial) !== JSON.stringify(initial)) {
    setSyncedInitial(initial);
    setValues(initial);
  }

  const dirty = JSON.stringify(values) !== JSON.stringify(initial);

  const save = async (): Promise<void> => {
    setError(null);
    const message = sheetInputMessage(values);
    setInvalidMessage(message);
    if (message) return;
    setSaving(true);
    try {
      await onSave(values);
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <SupplierFields
        value={values}
        onChange={(supplier) => setValues({ ...values, ...supplier })}
      />

      <div className={`${formStyles.field} sm:max-w-xs`}>
        <Label htmlFor="production-order-due-date">납기 희망일</Label>
        <Input
          id="production-order-due-date"
          type="date"
          value={values.dueDate ?? ''}
          onChange={(event) =>
            setValues({ ...values, dueDate: event.target.value === '' ? null : event.target.value })
          }
        />
      </div>

      <LineRowsEditor lines={values.lines} onChange={(lines) => setValues({ ...values, lines })} />

      <div className={formStyles.field}>
        <Label htmlFor="production-order-note">요청 사항</Label>
        <textarea
          id="production-order-note"
          className={formStyles.textarea}
          maxLength={MAX_PRODUCTION_ORDER_SHEET_NOTE_LENGTH}
          value={values.note}
          onChange={(event) => setValues({ ...values, note: event.target.value })}
        />
      </div>

      {invalidMessage || error ? (
        <p role="alert" className="text-sm text-destructive">
          {invalidMessage ?? orderErrorMessage(error)}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={disabled || saving} isLoading={saving}>
          저장
        </Button>
        <PrintProductionOrderButton href={printHref} />
        {dirty ? (
          <p className={formStyles.hint}>
            저장하지 않은 변경이 있습니다. 출력은 저장된 내용으로 합니다.
          </p>
        ) : null}
      </div>
    </form>
  );
};
